import React, { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react'

import Product, { ProductLite } from '@src/quince-site-common/src/domains/Product'
import Bundle, { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { getFestivalOfferPrice } from '@src/quince-site-common/src/utils/festivalOffersUtility'

import { getInitialSelectedOptions } from './get-initial-selected-options.utils'

interface ProductCardCommonType {
  product: Product | ProductLite | Bundle
}

type ProductCardContextType = {
  selectedOptions: ProductVariantOption[]
  setSelectedOptions: React.Dispatch<React.SetStateAction<ProductVariantOption[]>>
  siteSearchQuery?: string
  productPosition?: number
  productSearchObjectId: string
  festivalPrice?: number
  salePrice: number
} & ProductCardCommonType
const productCardContext = createContext<ProductCardContextType | null>(null)

type ProductCardContextProviderProps = {
  initialSelectedOptions?: ProductVariantOption[]
  siteSearchQuery?: string
  productPosition?: number
} & ProductCardCommonType
export const ProductCardContextProvider: FC<PropsWithChildren<ProductCardContextProviderProps>> = props => {
  const { product, initialSelectedOptions, siteSearchQuery, productPosition } = props
  const [selectedOptions, setSelectedOptions] = useState(
    getInitialSelectedOptions({
      product,
      selectedOptions: initialSelectedOptions,
    }),
  )
  const [productSearchObjectId, setProductSearchObjectId] = useState('')

  useEffect(() => {
    setSelectedOptions(
      getInitialSelectedOptions({
        product,
        selectedOptions: initialSelectedOptions,
      }),
    )
  }, [product, initialSelectedOptions])

  useEffect(() => {
    if (product.shopifyProductId) {
      setProductSearchObjectId(`${product.shopifyProductId}_${selectedOptions[0]?.value}`)
    }
  }, [product, selectedOptions])

  const salePrice =
    product.type === PRODUCT_TYPES.BUNDLE
      ? productHelper.getQuincePriceForBundleOptions(product, selectedOptions)
      : productHelper.getQuincePriceForOptions(product, selectedOptions)

  const festivalPrice = useMemo(
    () => getFestivalOfferPrice({ product, selectedOptions }) ?? undefined,
    [product, selectedOptions],
  )

  const value = useMemo((): ProductCardContextType => {
    return {
      product,
      selectedOptions,
      setSelectedOptions,
      siteSearchQuery,
      productPosition,
      productSearchObjectId,
      festivalPrice,
      salePrice,
    }
  }, [product, selectedOptions, festivalPrice, salePrice, productPosition])

  return <productCardContext.Provider value={value}>{props.children}</productCardContext.Provider>
}

export function useProductCardContext() {
  const ctx = useContext(productCardContext)
  if (!ctx) {
    throw new Error('useProductCardContext must be called from within ProductCardContextProvider')
  }
  return ctx
}
